/**
 * NÚMEROS, DATAS E DINHEIRO NO JEITO BRASILEIRO — lidos e escritos num lugar só.
 */

/**
 * Texto do teclado virando número. Aceita «512,5», «1.050» (mil e cinquenta),
 * «1.050,5» e «512.5». Devolve null quando não é número.
 */
export function lerNumero(texto: string): number | null {
  let t = String(texto ?? '').trim().replace(/\s/g, '').replace('−', '-')
  if (t === '') return null
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.')
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, '')
  if (!/^-?\d*\.?\d+$/.test(t)) return null
  const v = Number(t)
  return Number.isFinite(v) ? v : null
}

/** Número para colocar de volta num campo: 512.5 → «512,5». */
export function paraCampo(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '' : String(n).replace('.', ',')
}

/**
 * R2B · Dinheiro de volta num campo, com separador de milhar e duas casas:
 * 51911.7 → «51.911,70» (a parcela lida de relance). O `lerNumero` lê de volta.
 */
export function paraCampoEmReais(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n) ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function num(n: number | null | undefined, casas = 0): string {
  return n === null || n === undefined || !Number.isFinite(n)
    ? '—'
    : n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
}

/** Peso: sem casa decimal quando é inteiro. */
export function kg(n: number | null | undefined): string {
  return num(n, n !== null && n !== undefined && n % 1 ? 1 : 0)
}

export function reais(n: number | null | undefined): string {
  return n === null || n === undefined || !Number.isFinite(n)
    ? '—'
    : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const dois = (n: number) => String(n).padStart(2, '0')

/** Data de hoje no fuso do aparelho, «2026-10-03». */
export function hoje(): string {
  const d = new Date()
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`
}

export function agora(): string {
  return new Date().toISOString()
}

/** «2026-10-03» → «03/10/2026» */
export function dataBR(s: string | null | undefined): string {
  if (!s) return ''
  const [a, m, d] = s.slice(0, 10).split('-')
  return `${d}/${m}/${a}`
}

/** Instante ISO → «03/10/2026 14:32» no fuso do aparelho. */
export function quando(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()} ${dois(d.getHours())}:${dois(d.getMinutes())}`
}

export function hora(iso: string): string {
  const d = new Date(iso)
  return `${dois(d.getHours())}:${dois(d.getMinutes())}`
}

/** Instante ISO → dia local «2026-10-03». */
export function diaDe(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${dois(d.getMonth() + 1)}-${dois(d.getDate())}`
}

export const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']

export function diaDaSemana(s: string): string {
  const [a, m, d] = s.split('-').map(Number)
  return DIAS[new Date(a!, m! - 1, d!).getDay()]!
}

/**
 * R1C · Plural do nome da categoria para o WhatsApp e os avisos: «53 bois»,
 * «20 vacas», «2 vacas com cria», «3 bois magros» (antes saía «53 boi»). Só
 * as palavras antes de «com/de/da/do» vão para o plural; o resto fica.
 */
export function plural(texto: string, n: number): string {
  if (n === 1) return texto
  const palavras = texto.split(' ')
  const corte = palavras.findIndex((p, i) => i > 0 && /^(com|de|da|do|das|dos|e|sem)$/i.test(p))
  const ate = corte < 0 ? palavras.length : corte
  return palavras.map((p, i) => (i < ate ? pluralDaPalavra(p) : p)).join(' ')
}

function pluralDaPalavra(p: string): string {
  if (!p || /s$/i.test(p)) return p
  if (/ão$/i.test(p)) return p.replace(/ão$/i, 'ões')
  if (/[aeiou]l$/i.test(p)) return p.replace(/l$/i, 'is')
  if (/m$/i.test(p)) return p.replace(/m$/i, 'ns')
  if (/[rz]$/i.test(p)) return p + 'es'
  return p + 's'
}

/** Para busca: sem acento, sem maiúscula. */
export function normalizar(s: string | null | undefined): string {
  return String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim()
}

/**
 * 3D · NOME PARECIDO — para não duplicar o mesmo lugar ou pessoa no cadastro
 * («Faz. Boa Vista» e «Fazenda Boavista»). Tira acento, pontuação e as
 * palavras que não distinguem um nome de outro (fazenda, sítio, de, da…) e
 * aceita um ou dois erros de digitação. Só AVISA: quem decide é a pessoa —
 * dois sítios «São José» existem de verdade.
 */
const GENERICAS = /\b(fazenda|faz|fz|sitio|sit|chacara|estancia|agropecuaria|agro|ltda|sr|sra|dona|seu|de|da|do|das|dos|e)\b/g

function chaveDeNome(s: string): string {
  return normalizar(s).replace(/[^a-z0-9 ]/g, ' ').replace(GENERICAS, ' ').replace(/\s+/g, '')
}

function distancia(a: string, b: string): number {
  let antes = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const agora = [i]
    for (let j = 1; j <= b.length; j++) {
      agora[j] = Math.min(antes[j]! + 1, agora[j - 1]! + 1, antes[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1))
    }
    antes = agora
  }
  return antes[b.length]!
}

/** Um nome da lista parecido com o digitado — mas não igual (igual é «do cadastro»). */
export function nomeParecido<T extends { nome: string }>(lista: T[], nome: string): T | undefined {
  const n = normalizar(nome)
  const k = chaveDeNome(nome)
  if (k.length < 3) return undefined
  const outros = lista.filter((x) => normalizar(x.nome) !== n)
  const mesmo = outros.find((x) => chaveDeNome(x.nome) === k)
  if (mesmo) return mesmo
  const proximo = outros.find((x) => {
    const kx = chaveDeNome(x.nome)
    const limite = Math.min(k.length, kx.length) >= 8 ? 2 : 1
    return kx.length >= 3 && Math.abs(k.length - kx.length) <= limite && distancia(k, kx) <= limite
  })
  if (proximo) return proximo
  // um nome é o começo do outro, palavra por palavra («João Silva» × «João
  // Silva – Faz. Boa Vista»): na simulação de 06/10/2026 o aviso não aparecia
  // e o cadastro duplicaria. Só com 2 palavras ou mais, para «João» sozinho
  // não casar com todo João.
  const palavras = (s: string) => normalizar(s).split(/[^a-z0-9]+/).filter(Boolean)
  const pn = palavras(nome)
  if (pn.length < 2) return undefined
  const comeca = (a: string[], b: string[]) => a.length < b.length && a.every((w, i) => w === b[i])
  return outros.find((x) => {
    const px = palavras(x.nome)
    return px.length >= 2 && (comeca(pn, px) || comeca(px, pn))
  })
}

/**
 * Identidade de ficha, produtor, pagamento… nasce no aparelho (registro feito
 * sem sinal já tem nome). Sorteada de verdade (UUID): com a hora no meio, dois
 * aparelhos de empresas diferentes podiam se esbarrar no banco.
 */
export function novoId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
}
